import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { keepPreviousData } from '@tanstack/react-query'
import { usersAPI, type UsersFilter } from '../api/users'
import toast from 'react-hot-toast'

export const useUsers = (filters: UsersFilter = {}) =>
  useQuery({
    queryKey: ['users', filters],
    queryFn: () => usersAPI.getAll(filters),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  })

export const useUserDetail = (id: string) =>
  useQuery({
    queryKey: ['user', id],
    queryFn: () => usersAPI.getById(id),
    enabled: !!id,
  })

export const useSuspendUser = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, suspended, reason }: { id: string; suspended: boolean; reason?: string }) =>
      usersAPI.setSuspended(id, suspended, reason),
    onSuccess: (_, { suspended }) => {
      toast.success(suspended ? 'Compte suspendu' : 'Compte réactivé')
      qc.invalidateQueries({ queryKey: ['users'] })
      qc.invalidateQueries({ queryKey: ['user'] })
    },
    onError: () => toast.error('Erreur lors de la mise à jour'),
  })
}

export const useUpdateUser = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      usersAPI.update(id, data),
    onSuccess: () => {
      toast.success('Profil mis à jour')
      qc.invalidateQueries({ queryKey: ['users'] })
      qc.invalidateQueries({ queryKey: ['user'] })
    },
    onError: () => toast.error('Erreur lors de la mise à jour'),
  })
}

export const useDeleteUser = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => usersAPI.delete(id),
    onSuccess: () => {
      toast.success('Utilisateur supprimé')
      qc.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        'Erreur lors de la suppression'
      toast.error(msg)
    },
  })
}
